import {memo, useRef, useState} from 'react'
import {clientTimeZoneOffset, loadingType, DEFAULT_WORKER} from 'app/utils/common/constants'
import {DatePackageAvailable, Worker} from 'app/modules/tickets/models/OrderDrawerModel'
import {FormikErrors, FormikHelpers} from 'formik'
import {useEffect, useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import ServiceProviderContainer from '../../../../../../shared/ServiceProvider/components/Container'
import {ReschedulePackageForm} from '../type'
import {TicketModel} from '../../../models'
import {
  sortServiceProviders,
  transformWorkerData,
  pickRouteOptimizationCfg,
  pickRouteOptimizationCfgFromWorkers,
  resolveVisitReminderFromWorkers,
} from 'app/utils/helpers/index'
import {
  buildRescheduleData,
  buildRescheduleDayRequest,
  buildWorkerStartDateMap,
  fetchRescheduleCalendarDays,
  requestDayReach,
} from '../../../redux/RescheduleAvailability'
import moment from 'moment'

const ServiceProviderComponent = (props: {
  values: ReschedulePackageForm
  errors: FormikErrors<ReschedulePackageForm>
  setValues: FormikHelpers<ReschedulePackageForm>['setValues']
  language: string
  index: number
  id: string
  name: string
  ticketData: TicketModel | null
}) => {
  const {t} = useTranslation()

  const {values, setValues, language, index, id, name, ticketData} = props

  const [isSpAvailble, setIsSpAvailble] = useState<boolean>(true)

  // V2 per-worker day calendars, cached per worker+filter to avoid refetching on toggle.
  const workerCalendarCache = useRef(
    new Map<string, {days: DatePackageAvailable[]; loadedUntilDay: number}>()
  )

  // Normalize isAvailable (API sometimes returns 0/1 or string)
  const isAvailableValue = (v: any) => v === true || v === 1 || v === '1' || v === 'true'
  const getEarliestAvailableDateFromDays = (days?: any[]): Date | undefined => {
    if (!days || days.length === 0) return undefined
    const first = [...days]
      .filter((d) => isAvailableValue(d?.isAvailable))
      .sort((a, b) => new Date(a.dateCheck).getTime() - new Date(b.dateCheck).getTime())[0]
    return first ? new Date(first.dateCheck) : undefined
  }

  const fetchData = async (startDate?: Date) => {
    if (!ticketData) return
    try {
      setValues((s) => ({...s, loadingType: loadingType.SERVICE_PROVIDER}))

      const dayRequest = buildRescheduleDayRequest(
        {
          ticketId: ticketData.id,
          shiftType: values.service_provider_filter.filterBy,
          clientTimeOffset: -clientTimeZoneOffset,
          selectWorkerId: '',
        },
        startDate ?? values.service_provider_filter.startDate
      )
      const {calendar, days} = await fetchRescheduleCalendarDays(dayRequest)

      if (!calendar || (!calendar.workers?.length && !days.length)) {
        setIsSpAvailble(false)
        setValues((s) => ({...s, loadingType: undefined}))
        return
      }

      const data = buildRescheduleData(calendar, days, ticketData.numberOfWorker, {
        baseOnAvailability: t('CREATE_ORDER_DRAWER.BASE_ON_AVAILABILITY'),
      })
      const startDateByWorkerId = buildWorkerStartDateMap(calendar)
      workerCalendarCache.current.clear()

      if (!data.workers) {
        setIsSpAvailble(false)
        setValues((s) => ({...s, loadingType: undefined}))
        return
      }

      const formatData = transformWorkerData(
        data.workers.map((item) =>
          item.workerId
            ? item
            : {
                ...item,
                workerId: DEFAULT_WORKER,
                isAuto: true,
                personalImageUrl: '/assets/images/common/unknown-worker.png',
                workerNameEn: t('CREATE_ORDER_DRAWER.BASE_ON_AVAILABILITY'),
                workerNameAr: t('CREATE_ORDER_DRAWER.BASE_ON_AVAILABILITY'),
              }
        )
      )

      // startDate per worker: from its own day list, else the workerStartDates map.
      const normalizedWorkers = formatData.map((w: any) => {
        const earliest = getEarliestAvailableDateFromDays(w?.listAvailableDay)
        return {
          ...w,
          startDate:
            earliest ??
            w?.startDate ??
            startDateByWorkerId.get(String(w?.workerId ?? '').toUpperCase()),
        }
      })

      // "Based on Availability" item (baseOn) should display earliest available date across ALL SPs
      const baseOnRaw = normalizedWorkers.find((item: any) => item.isAuto)
      const otherOption = normalizedWorkers.filter((item: any) => !item.isAuto)

      const earliestAcrossAll = (() => {
        const dates = normalizedWorkers
          .map((w: any) => getEarliestAvailableDateFromDays(w?.listAvailableDay))
          .filter(Boolean) as Date[]
        if (!dates.length) return undefined
        return new Date(Math.min(...dates.map((d) => d.getTime())))
      })()

      const baseOn = baseOnRaw
        ? {
            ...baseOnRaw,
            startDate: earliestAcrossAll ?? baseOnRaw.startDate,
          }
        : baseOnRaw

      const service_provider_data = sortServiceProviders(
        values.service_provider_filter,
        otherOption
      )

      const workerBindOptions = {
        workerId:
          values.service_provider && values.service_provider !== DEFAULT_WORKER
            ? values.service_provider
            : undefined,
        spId: ticketData.spId,
      }

      setValues((s) => {
        const updates: Partial<ReschedulePackageForm> = {
          service_provider_available: service_provider_data,
          initial_fav_worker_ids: service_provider_data
            .filter((w) => w.isFavoriteWorker)
            .map((w) => w.workerId),
          calendarLoadedUntilDay: requestDayReach(dayRequest),
          number_of_workers: data.numberOfWorker,
          loadingType: undefined,
          isShowDeliveryTime: data.isShowDeliveryTime,
          visitReminder: resolveVisitReminderFromWorkers(
            data.workers ?? undefined,
            workerBindOptions,
            data.visitReminder,
          ),
          routeOptimizationCfg:
            pickRouteOptimizationCfgFromWorkers(data.workers ?? undefined, workerBindOptions) ??
            pickRouteOptimizationCfg(data),
        }
        
        // When "Based on Availability" is selected, ensure `.start-date` in SP list shows earliest available date (merged across all SPs)
        const isBasedOnAvailability = !s.service_provider || s.service_provider === DEFAULT_WORKER
        
        if (!isBasedOnAvailability) {
          // When specific worker is selected, use baseOn.startDate
          updates.defaultServiceProvider = baseOn
          if (!s.filterStartDate) {
            updates.filterStartDate = baseOn?.startDate
          }
          if (!s.service_provider_filter.startDate) {
            updates.service_provider_filter = {...s.service_provider_filter, startDate: baseOn?.startDate}
          }
        } else {
          // Always update defaultServiceProvider to the current `baseOn` (with computed startDate),
          // but avoid unnecessary updates to prevent extra renders.
          const current = s.defaultServiceProvider?.startDate
            ? moment(s.defaultServiceProvider.startDate).format('YYYY-MM-DD')
            : null
          const next = baseOn?.startDate ? moment(baseOn.startDate).format('YYYY-MM-DD') : null
          if (current !== next || s.defaultServiceProvider?.workerId !== DEFAULT_WORKER) {
            updates.defaultServiceProvider = baseOn
          }
        }
        
        return {
          ...s,
          ...updates,
        }
      })
    } catch (error) {
      setValues((s) => ({...s, loadingType: undefined}))
      return
    }
  }

  const handleFavoriteWorker = (worker: Worker) => {
    const new_sp_available = values.service_provider_available.map((e) =>
      e.workerId === worker.workerId ? {...e, isFavoriteWorker: !e.isFavoriteWorker} : e
    )
    setValues((s) => ({
      ...s,
      service_provider_available: new_sp_available,
    }))
  }

  const displayedServiceProvider = useMemo(() => {
    if (
      values.service_provider_available &&
      values.number_of_workers &&
      values.defaultServiceProvider
    ) {
      return values.number_of_workers === 1
        ? [values.defaultServiceProvider, ...values.service_provider_available]
        : [values.defaultServiceProvider]
    }

    return []
  }, [values.service_provider_available])

  const trans = {
    title: `${index}. ${t('RESCHEDULE_TICKET.PROVIDER_SECTION.TITLE')}`,
    section_title: t('RESCHEDULE_TICKET.PROVIDER_SECTION.SUB_TITLE'),
    search_placeholder: t('CREATE_ORDER_DRAWER.SECTION_4.SEARCH_WORKER_PLACEHOLDER'),
  }

  const filterDisabled = displayedServiceProvider.length < 2

  const handleChangeDate = (startDate: Date) => fetchData(startDate)

  useEffect(() => {
    fetchData()
  }, [])

  useEffect(() => {
    if (
      values.loadingType !== loadingType.SERVICE_PROVIDER &&
      values.service_provider_available.length
    ) {
      const service_provider_data = sortServiceProviders(
        values.service_provider_filter,
        values.service_provider_available
      )
      setValues((s) => ({...s, service_provider_available: service_provider_data}))
    }
  }, [values.service_provider_filter.sortBy])

  useEffect(() => {
    values.loadingType !== loadingType.SERVICE_PROVIDER && fetchData()
  }, [values.service_provider_filter.filterBy])

  // Lazily fetch a specific worker's day calendar on selection and fill their entry.
  useEffect(() => {
    if (!ticketData) return
    const workerId = values.service_provider
    if (!workerId || workerId === DEFAULT_WORKER) return

    const entry = values.service_provider_available.find((w) => w.workerId === workerId)
    if (!entry || (entry.listAvailableDay && entry.listAvailableDay.length > 0)) return

    const applyDays = (days: DatePackageAvailable[], loadedUntilDay: number) => {
      const firstAvailable = days.find((d) => isAvailableValue(d?.isAvailable))
      setValues((s) => ({
        ...s,
        loadingType: undefined,
        calendarLoadedUntilDay: loadedUntilDay,
        service_provider_available: s.service_provider_available.map((w) =>
          w.workerId === workerId
            ? {
                ...w,
                listAvailableDay: days,
                startDate: firstAvailable ? new Date(firstAvailable.dateCheck) : w.startDate,
              }
            : w
        ),
      }))
    }

    const filterStart = values.service_provider_filter.startDate
    const cacheKey = `${workerId}|${values.service_provider_filter.filterBy}|${
      filterStart ? moment(filterStart).format('YYYY-MM-DD') : ''
    }`
    const cached = workerCalendarCache.current.get(cacheKey)
    if (cached) {
      applyDays(cached.days, cached.loadedUntilDay)
      return
    }

    let cancelled = false
    ;(async () => {
      try {
        setValues((s) => ({...s, loadingType: loadingType.DATE_TIME}))
        const dayRequest = buildRescheduleDayRequest(
          {
            ticketId: ticketData.id,
            shiftType: values.service_provider_filter.filterBy,
            clientTimeOffset: -clientTimeZoneOffset,
            selectWorkerId: workerId,
          },
          filterStart
        )
        const {days} = await fetchRescheduleCalendarDays(dayRequest)
        if (cancelled) return
        workerCalendarCache.current.set(cacheKey, {days, loadedUntilDay: requestDayReach(dayRequest)})
        applyDays(days, requestDayReach(dayRequest))
      } catch (error) {
        if (!cancelled) setValues((s) => ({...s, loadingType: undefined}))
      }
    })()

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.service_provider])

  return (
    <ServiceProviderContainer
      isLoading={values.loadingType === loadingType.SERVICE_PROVIDER}
      isSpAvailble={isSpAvailble}
      displayedData={displayedServiceProvider}
      filterDisabled={filterDisabled}
      handleFavoriteWorker={handleFavoriteWorker}
      trans={trans}
      id={id}
      name={name}
      filterStartDate={values.filterStartDate}
      handleChangeDate={handleChangeDate}
    />
  )
}

export default memo(ServiceProviderComponent)
